/**
 * Admin · Translation feedback inbox.
 *
 * Lists reviewer suggestions from public site visitors with locale + status
 * filters. Admin can triage (new → triaged → applied / wontfix) and attach
 * a private note.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Languages, CheckCircle2, Clock, XCircle, Mail } from "lucide-react";
import { api } from "../../lib/api";
import { AdminHeader } from "./_shared";
import { LOCALES } from "../../lib/locales-config";

const STATUSES = [
  { value: "new", label: "New", Icon: Clock, tone: "text-[#B58A3F] bg-[#F4E5C5]/60" },
  { value: "triaged", label: "Triaged", Icon: Languages, tone: "text-[#1F3A2A] bg-[#E6EFE8]" },
  { value: "applied", label: "Applied", Icon: CheckCircle2, tone: "text-[#1F3A2A] bg-[#D1F1D6]" },
  { value: "wontfix", label: "Won't fix", Icon: XCircle, tone: "text-[#6B7280] bg-[#EEF0F4]" },
];

function localeAutonym(code) {
  return LOCALES[code]?.autonym || code;
}

export default function AdminTranslationFeedback() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ locale: "", status: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.locale) params.locale = filter.locale;
      if (filter.status) params.status_filter = filter.status;
      const r = await api.get("/admin/translation-feedback", { params });
      setItems(r.data || []);
      const s = await api.get("/admin/translation-feedback/summary");
      setSummary(s.data);
    } catch (e) {
      toast.error("Could not load feedback.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.locale, filter.status]);

  const localeCodes = useMemo(
    () => Object.keys(summary?.by_locale || {}).sort(),
    [summary]
  );

  const updateOne = async (id, patch) => {
    try {
      const r = await api.patch(`/admin/translation-feedback/${id}`, patch);
      setItems((prev) => prev.map((it) => (it.id === id ? r.data : it)));
      toast.success("Updated.");
    } catch (e) {
      toast.error("Could not update.");
    }
  };

  return (
    <div data-testid="admin-translation-feedback" className="space-y-8">
      <AdminHeader
        eyebrow="Translation feedback"
        title="What visitors are telling us"
      />
      <p className="text-sm text-[#6B7280] -mt-4 max-w-2xl">
        Each note is a calm suggestion from someone reading Alouvya in their
        language. Triage gently.
      </p>

      {summary && (
        <section
          data-testid="admin-translation-feedback-summary"
          className="rounded-2xl border border-[#EEF0F4] bg-white p-6"
        >
          <div className="eyebrow">By locale</div>
          <div className="mt-4 flex flex-wrap gap-3">
            {Object.entries(summary.by_locale).length === 0 ? (
              <p className="text-sm text-[#6B7280]">No feedback yet.</p>
            ) : (
              Object.entries(summary.by_locale).map(([code, count]) => (
                <button
                  key={code}
                  data-testid={`admin-feedback-pill-${code}`}
                  onClick={() =>
                    setFilter((f) => ({ ...f, locale: f.locale === code ? "" : code }))
                  }
                  className={
                    "rounded-full px-4 py-1.5 text-sm border transition-colors " +
                    (filter.locale === code
                      ? "bg-[#1F3A2A] text-white border-[#1F3A2A]"
                      : "bg-white text-[#1A1B2E] border-[#EEF0F4] hover:border-[#1F3A2A]/40")
                  }
                >
                  {localeAutonym(code)} · {count}
                </button>
              ))
            )}
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="eyebrow">Status</div>
          {STATUSES.map((s) => (
            <button
              key={s.value}
              data-testid={`admin-feedback-status-${s.value}`}
              onClick={() =>
                setFilter((f) => ({ ...f, status: f.status === s.value ? "" : s.value }))
              }
              className={
                "rounded-full px-3 py-1 text-xs font-semibold transition-all " +
                (filter.status === s.value
                  ? "bg-[#1F3A2A] text-white"
                  : "bg-[#F7F8FC] text-[#6B7280] hover:bg-[#EEF0F4]")
              }
            >
              {s.label}
            </button>
          ))}
          {(filter.locale || filter.status) && (
            <button
              data-testid="admin-feedback-clear-filters"
              onClick={() => setFilter({ locale: "", status: "" })}
              className="ml-auto text-xs text-[#1F3A2A] underline underline-offset-4"
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      <section className="space-y-3" data-testid="admin-translation-feedback-list">
        {loading ? (
          <p className="text-sm text-[#6B7280] italic">Loading…</p>
        ) : items.length === 0 ? (
          <p
            data-testid="admin-translation-feedback-empty"
            className="rounded-2xl border border-dashed border-[#EEF0F4] bg-white p-10 text-center text-sm text-[#6B7280]"
          >
            All quiet here. No feedback matches these filters.
          </p>
        ) : (
          items.map((item) => (
            <FeedbackRow key={item.id} item={item} onUpdate={updateOne} />
          ))
        )}
      </section>
    </div>
  );
}

function FeedbackRow({ item, onUpdate }) {
  const [adminNote, setAdminNote] = useState(item.admin_note || "");
  const [savingNote, setSavingNote] = useState(false);
  const status = STATUSES.find((s) => s.value === item.status);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await onUpdate(item.id, { admin_note: adminNote });
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <article
      data-testid={`admin-translation-feedback-row-${item.id}`}
      className="rounded-2xl border border-[#EEF0F4] bg-white p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1F3A2A]">
            {localeAutonym(item.locale)} · {item.locale}
          </div>
          <p className="mt-2 text-xs text-[#6B7280]">
            {new Date(item.received_at).toLocaleString()}
            {item.surface && (
              <>
                {" · "}
                <span className="font-mono">{item.surface}</span>
              </>
            )}
            {item.source_key && (
              <>
                {" · "}
                <code className="text-[10px]">{item.source_key}</code>
              </>
            )}
          </p>
        </div>
        {status && (
          <span
            data-testid={`admin-translation-feedback-status-${item.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase ${status.tone}`}
          >
            <status.Icon size={12} strokeWidth={2} />
            {status.label}
          </span>
        )}
      </header>

      <blockquote
        dir="auto"
        className="mt-4 rounded-xl bg-[#F7F8FC] border border-[#EEF0F4] p-4 text-sm text-[#1A1B2E] leading-relaxed whitespace-pre-wrap"
      >
        {item.suggestion}
      </blockquote>

      {item.reviewer_email && (
        <p className="mt-3 text-xs text-[#6B7280] flex items-center gap-1.5">
          <Mail size={12} strokeWidth={1.6} />
          <a
            href={`mailto:${item.reviewer_email}`}
            className="underline underline-offset-2 hover:text-[#1A1B2E]"
          >
            {item.reviewer_email}
          </a>
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            data-testid={`admin-translation-feedback-set-${item.id}-${s.value}`}
            disabled={item.status === s.value}
            onClick={() => onUpdate(item.id, { status: s.value })}
            className={
              "text-xs font-semibold rounded-full px-3 py-1 transition-all " +
              (item.status === s.value
                ? "bg-[#1F3A2A] text-white cursor-default"
                : "bg-white border border-[#EEF0F4] text-[#1A1B2E] hover:border-[#1F3A2A]")
            }
          >
            {s.label}
          </button>
        ))}
      </div>

      <label className="block mt-4">
        <span className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[#6B7280] mb-1.5">
          Admin note (private)
        </span>
        <textarea
          data-testid={`admin-translation-feedback-note-${item.id}`}
          rows={2}
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          maxLength={600}
          className="input-soft w-full text-sm"
          placeholder="e.g. forwarded to fr-FR reviewer · matches an existing report"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            data-testid={`admin-translation-feedback-save-note-${item.id}`}
            onClick={saveNote}
            disabled={savingNote || adminNote === (item.admin_note || "")}
            className="text-xs font-semibold text-[#1F3A2A] disabled:text-[#9CA3AF] underline underline-offset-4"
          >
            {savingNote ? "Saving…" : "Save note"}
          </button>
        </div>
      </label>
    </article>
  );
}
